import { useCallback, useEffect, useState } from "react";
import { Ticket, Trash2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { CouponAlerts, usDate } from "@/components/CouponAlerts";
import { t } from "@/i18n";

const CHIP = { expired: ["chip-err", "Expired"], expires_today: ["chip-warn", "Expires today"], expires_tomorrow: ["chip-warn", "Expires tomorrow"], active: ["chip-ok", "Active"] };

function AddCoupon({ api, onAdded }) {
  const [f, setF] = useState({ code: "", expires_on: "", note: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/coupons", f); setF({ code: "", expires_on: "", note: "" }); toast.success(t("Coupon added")); onAdded(); }
    catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="card grid sm:grid-cols-[1fr_auto_2fr_auto] gap-2 items-end" data-testid="coupon-add-form">
      <label className="stack-sm"><span className="mono-label">{t("Code")}</span>
        <input className="field uppercase" required value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="KRAB20" maxLength={24} data-testid="coupon-code-input" /></label>
      <label className="stack-sm"><span className="mono-label">{t("Last valid day")}</span>
        <input className="field" type="date" required value={f.expires_on} onChange={(e) => setF({ ...f, expires_on: e.target.value })} data-testid="coupon-expiry-input" /></label>
      <label className="stack-sm"><span className="mono-label">{t("Note")}</span>
        <input className="field" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder={t("e.g. 20% off any Krabby Patty")} maxLength={200} data-testid="coupon-note-input" /></label>
      <button className="btn btn-primary" disabled={busy} data-testid="coupon-add-button">{busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {t("Add")}</button>
    </form>
  );
}

export default function Coupons() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const load = useCallback(() => api.get("/coupons").then((r) => { setData(r.data); setTick((n) => n + 1); })
    .catch((e) => setData({ forbidden: errMsg(e), coupons: [] })), [api]);
  useEffect(() => { load(); }, [load]);
  const patch = async (id, body) => { try { await api.patch(`/coupons/${id}`, body); load(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (c) => {
    if (!window.confirm(t("Delete coupon {code}?", { code: c.code }))) return;
    try { await api.delete(`/coupons/${c.id}`); toast.success(t("Coupon removed")); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (data?.forbidden) return <div className="max-w-3xl mx-auto px-4 py-8"><p className="card faint" data-testid="coupons-forbidden">{data.forbidden}</p></div>;
  return (
    <div className="max-w-3xl mx-auto px-4 py-8 stack" data-testid="coupons-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight rise flex items-center gap-3"><Ticket size={28} /> {t("Coupons")}</h1>
        <p className="muted mt-1">{t("Kept in the Discounts and coupons OI. The owner and cashiers are warned a day before a code expires.")}</p>
      </div>
      <CouponAlerts refresh={tick} />
      {data?.can_edit && <AddCoupon api={api} onAdded={load} />}
      {!data ? <p className="faint">{t("Loading…")}</p> : data.coupons.length === 0 ? <p className="card faint" data-testid="coupons-empty">{t("No coupons yet.")}</p> : (
        <div className="card !p-0 overflow-hidden" data-testid="coupons-list">
          {data.coupons.map((c) => {
            const [cls, label] = CHIP[c.status];
            return (
              <div key={c.id} className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-[var(--border-color)] first:border-t-0" data-testid={`coupon-row-${c.code}`}>
                <span className="font-mono font-bold w-28">{c.code}</span>
                <span className={`chip ${cls}`} data-testid={`coupon-status-${c.code}`}>{t(label)}</span>
                {data.can_edit
                  ? <input type="date" className="field !w-auto !py-1 text-sm" defaultValue={c.expires_on} key={c.expires_on}
                      onBlur={(e) => e.target.value && e.target.value !== c.expires_on && patch(c.id, { expires_on: e.target.value })} data-testid={`coupon-expiry-edit-${c.code}`} />
                  : <span className="text-sm muted">{t("Last valid day")}: {usDate(c.expires_on)}</span>}
                <span className="flex-1 text-sm muted min-w-[8rem]">{c.note}</span>
                {data.can_edit && <button className="btn btn-ghost btn-sm" onClick={() => remove(c)} aria-label={t("Remove")} data-testid={`coupon-delete-${c.code}`}><Trash2 size={14} /></button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
